import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useServices } from "@/hooks/useSiteContent";
import ContentStatus from "@/components/feature/ContentStatus";

const formatPrice = (price: number) =>
  new Intl.NumberFormat("vi-VN").format(price);

export default function ServiceList() {
  const { data: services, loading, error, reload } = useServices();
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="w-full bg-background-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div
          className={`text-center mb-12 md:mb-16 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-primary-100 text-primary-700 text-xs md:text-sm font-label font-medium rounded-full mb-4">
            <i className="ri-sparkling-2-line" />
            Danh mục dịch vụ
          </span>
          <h2 className="font-display text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Dịch vụ chăm sóc sức khỏe của chúng tôi
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto font-body">
            Bốn liệu trình công nghệ cao được thiết kế riêng cho từng nhu cầu, giúp bạn khỏe đẹp tự nhiên và an toàn.
          </p>
        </div>

        {/* Service list */}
        <ContentStatus loading={loading} error={error} onRetry={reload}>
        <div className="flex flex-col gap-8 md:gap-12">
          {services.map((service, index) => {
            const isEven = index % 2 === 0;
            return (
              <article
                key={service.id}
                className={`bg-background-50 border border-background-200/70 rounded-lg overflow-hidden transition-all duration-700 ${
                  visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{
                  transitionDelay: visible ? `${index * 100}ms` : "0ms",
                }}
              >
                <div className="flex flex-col lg:flex-row">
                  {/* Image */}
                  <div
                    className={`relative w-full lg:w-[45%] h-56 sm:h-72 lg:h-auto ${
                      isEven ? "lg:order-1" : "lg:order-2"
                    }`}
                  >
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-4 left-4 flex items-center gap-2">
                      <span className="px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-label font-semibold rounded-md whitespace-nowrap">
                        {service.duration}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div
                    className={`w-full lg:w-[55%] p-6 md:p-8 lg:p-10 ${
                      isEven ? "lg:order-2" : "lg:order-1"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                        <i className="ri-leaf-line text-lg" />
                      </div>
                      <span className="text-xs font-label font-semibold text-primary-600 uppercase tracking-wider">
                        Dịch vụ {index + 1}
                      </span>
                    </div>

                    <h3 className="font-display text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                      {service.name}
                    </h3>

                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5 font-body">
                      {service.description}
                    </p>

                    {/* Benefits */}
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-6">
                      {service.benefits.map((benefit) => (
                        <li
                          key={benefit}
                          className="flex items-start gap-2 text-sm text-foreground-700 font-body"
                        >
                          <div className="w-5 h-5 flex items-center justify-center mt-0.5 text-primary-600 flex-shrink-0">
                            <i className="ri-checkbox-circle-fill" />
                          </div>
                          <span className="leading-snug">{benefit}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Price + CTA */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-5 border-t border-background-200/70">
                      <div className="flex items-end gap-2">
                        <span className="font-display text-2xl font-bold text-primary-600">
                          {formatPrice(service.promoPrice)}đ
                        </span>
                        <span className="text-sm text-foreground-400 line-through font-body">
                          {formatPrice(service.price)}đ
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Link
                          to={`/service/${service.slug}`}
                          className="inline-flex items-center gap-1.5 px-5 py-2.5 border border-primary-300 text-primary-700 hover:bg-primary-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors"
                        >
                          Chi tiết
                          <i className="ri-arrow-right-line" />
                        </Link>
                        <Link
                          to="/booking"
                          className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors"
                        >
                          Đặt lịch
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
        </ContentStatus>
      </div>
    </section>
  );
}